import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import Projects3D from './project3d/Projects3D';
import { PROJECTS } from '../constants';
import { ScrollContext } from './ScrollContainer';

/* --------------------------------------------------------------------------
 * PROJECT GATE STORYBOARD
 *
 *    0ms   gate is readable; 3D scene is visible but frozen
 *    0ms   Enter aligns Projects to the viewport, then locks the scroller
 *  100ms   copy recedes while the stone arch moves toward the viewer
 *  400ms   gate clears; 3D render loop and interactions become active
 *    0ms   Exit freezes the scene and restores the gate
 *  400ms   scroller unlocks at the exact position where it was locked
 * ------------------------------------------------------------------------ */

const GATE_STAGE = {
  CLOSED: 0,
  FOCUSING: 1,
  OPENING: 2,
  ACTIVE: 3,
  CLOSING: 4,
};

const GATE_TIMING = {
  transition: 0.4,
  copy: 0.2,
  button: 0.15,
};

const GATE_EASE = [0.16, 1, 0.3, 1];
const GATE_SPRING = { type: 'spring', stiffness: 300, damping: 30 };

const ProjectsContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: var(--color-portfolio-ink);
`;

const ProjectsTitle = styled.h2`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const SceneLayer = styled.div`
  position: absolute;
  inset: 0;
  transform: scale(${({ $active }) => ($active ? 1 : 1.012)});
  filter: ${({ $active }) => ($active ? 'none' : 'blur(5px) saturate(0.68) contrast(0.88)')};
  transition: transform 400ms cubic-bezier(0.16, 1, 0.3, 1), filter 400ms cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, filter;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SectionBadge = styled.div`
  position: absolute;
  top: max(24px, env(safe-area-inset-top));
  left: clamp(20px, 4vw, 40px);
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 4px;
  pointer-events: none;
`;

const BadgeTag = styled.span`
  font-family: var(--font-portfolio-mono);
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--color-portfolio-wine-light);
`;

const BadgeTitle = styled.h3`
  margin: 0;
  font-family: var(--font-portfolio-display);
  font-size: clamp(2rem, 4vw, 3.2rem);
  line-height: 0.9;
  text-transform: uppercase;
  color: var(--color-portfolio-silver-light);

  span {
    color: var(--color-portfolio-wine);
  }
`;

const PanHint = styled.div`
  position: absolute;
  left: 50%;
  bottom: max(24px, env(safe-area-inset-bottom));
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  padding: 0 16px;
  border: 1px solid rgba(117, 22, 40, 0.4);
  background: rgba(23, 17, 19, 0.85);
  border-radius: 50px;
  transform: translateX(-50%);
  color: var(--color-portfolio-silver);
  font-family: var(--font-portfolio-mono);
  font-size: 0.76rem;
  letter-spacing: 0.12em;
  pointer-events: none;
  backdrop-filter: blur(8px);
  animation: panHintPulse 2s ease-in-out infinite;

  @keyframes panHintPulse {
    0%, 100% { opacity: 0.6; transform: translateX(-50%) scale(1); }
    50% { opacity: 1; transform: translateX(-50%) scale(1.03); }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const GateLayer = styled(motion.div)`
  position: absolute;
  inset: 0;
  z-index: 30;
  overflow: hidden;
  display: grid;
  place-items: center;
  isolation: isolate;
  pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
`;

const GateAtmosphere = styled.div`
  position: absolute;
  inset: 0;
  z-index: -2;
  background: radial-gradient(
    circle at 50% 45%,
    rgba(236, 232, 233, 0.08) 0 36%,
    rgba(23, 17, 19, 0.14) 72%,
    rgba(23, 17, 19, 0.48) 100%
  );

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background:
      linear-gradient(180deg, rgba(23, 17, 19, 0.18), transparent 28%),
      radial-gradient(circle at center, transparent 54%, rgba(23, 17, 19, 0.18) 100%);
    pointer-events: none;
  }
`;

const GateFocusMist = styled.div`
  position: absolute;
  left: 50%;
  top: 48%;
  z-index: -1;
  width: min(48vw, 680px);
  height: min(68dvh, 680px);
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: radial-gradient(
    ellipse at center,
    rgba(236, 232, 233, 0.82) 0 24%,
    rgba(236, 232, 233, 0.58) 44%,
    rgba(236, 232, 233, 0.18) 64%,
    transparent 76%
  );
  backdrop-filter: blur(7px) saturate(0.72);
  pointer-events: none;

  @media (max-width: 768px) {
    width: min(92vw, 440px);
    height: min(58dvh, 520px);
  }
`;

const GateImage = styled(motion.img)`
  position: absolute;
  left: 50%;
  bottom: -1px;
  z-index: -1;
  width: auto;
  height: min(94dvh, 94%);
  max-width: none;
  transform-origin: center 76%;
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
  filter: drop-shadow(0 28px 34px rgba(23, 17, 19, 0.28));

  @media (max-width: 768px) {
    height: min(76dvh, 76%);
  }
`;

const GateKicker = styled(motion.p)`
  position: absolute;
  top: max(22px, env(safe-area-inset-top));
  left: clamp(20px, 4vw, 40px);
  margin: 0;
  font-family: var(--font-portfolio-mono);
  font-size: clamp(0.68rem, 1vw, 0.76rem);
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--color-portfolio-wine);
`;

const GateContent = styled(motion.div)`
  position: relative;
  z-index: 2;
  width: min(88vw, 580px);
  margin-top: clamp(-36px, -4vh, -16px);
  padding: 24px;
  text-align: center;
  color: var(--color-portfolio-ink);
`;

const GateNumber = styled.p`
  margin: 0 0 12px;
  font-family: var(--font-portfolio-mono);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: rgba(77, 13, 26, 0.62);
`;

const GateEyebrow = styled.p`
  margin: 0 0 8px;
  font-family: var(--font-portfolio-mono);
  font-size: clamp(0.66rem, 1vw, 0.76rem);
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-portfolio-ink);
`;

const GateTitle = styled.h3`
  margin: 0;
  font-family: var(--font-portfolio-display);
  font-size: clamp(2rem, 4.1vw, 4rem);
  line-height: 0.95;
  letter-spacing: -0.045em;
  text-transform: uppercase;
  color: var(--color-portfolio-wine);
  white-space: nowrap;

  @media (max-width: 768px) {
    max-width: 9ch;
    margin-inline: auto;
    white-space: normal;
  }
`;

const GateCopy = styled.p`
  max-width: 30ch;
  margin: 16px auto 22px;
  font-size: clamp(0.78rem, 1.2vw, 0.92rem);
  line-height: 1.5;
  color: rgba(23, 17, 19, 0.72);
`;

const EnterButton = styled(motion.button)`
  min-width: 132px;
  min-height: 44px;
  padding: 0 24px;
  border: 1px solid var(--color-portfolio-wine);
  border-radius: 999px;
  background: var(--color-portfolio-wine);
  box-shadow: 0 12px 30px rgba(77, 13, 26, 0.24);
  color: var(--color-portfolio-silver-light);
  font-family: var(--font-portfolio-mono);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background-color 100ms ease-out, box-shadow 100ms ease-out;

  &:hover {
    background: var(--color-portfolio-wine-dark);
    box-shadow: 0 14px 34px rgba(77, 13, 26, 0.32);
  }

  &:focus-visible {
    outline: 3px solid var(--color-portfolio-wine-dark);
    outline-offset: 4px;
  }

  &:disabled {
    cursor: wait;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ExitButton = styled(motion.button)`
  position: absolute;
  top: max(24px, env(safe-area-inset-top));
  right: clamp(20px, 4vw, 40px);
  z-index: 24;
  min-height: 44px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: 1px solid rgba(236, 232, 233, 0.34);
  border-radius: 999px;
  background: rgba(23, 17, 19, 0.86);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.24);
  color: var(--color-portfolio-silver-light);
  font-family: var(--font-portfolio-mono);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  backdrop-filter: blur(12px);
  transition: border-color 100ms ease-out, background-color 100ms ease-out;

  &::before {
    content: '×';
    display: grid;
    place-items: center;
    width: 20px;
    height: 20px;
    border: 1px solid rgba(236, 232, 233, 0.5);
    border-radius: 50%;
    font-family: Arial, sans-serif;
    font-size: 1rem;
    line-height: 1;
  }

  &:hover {
    border-color: var(--color-portfolio-wine-light);
    background: rgba(77, 13, 26, 0.94);
  }

  &:focus-visible {
    outline: 3px solid var(--color-portfolio-silver-light);
    outline-offset: 3px;
  }

  @media (max-width: 768px) {
    top: auto;
    right: 20px;
    bottom: max(20px, env(safe-area-inset-bottom));
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const MobileProjectsLayer = styled.div`
  display: none;
`;

const MobileHeader = styled.div`
  padding: 0 20px 16px;
  color: var(--color-portfolio-silver-light);
  pointer-events: none;
`;

const MobileEyebrow = styled.p`
  margin: 0 0 8px;
  font-family: var(--font-portfolio-mono);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--color-portfolio-wine-light);
`;

const MobileTitle = styled.h3`
  margin: 0;
  max-width: 10ch;
  font-family: var(--font-portfolio-display);
  font-size: clamp(2.25rem, 11vw, 3.8rem);
  line-height: 0.92;
  letter-spacing: 0;
  text-transform: uppercase;
`;

const MobileScroller = styled.div`
  display: flex;
  gap: 14px;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  scroll-padding: 20px;
  padding: 0 20px 8px;
  pointer-events: auto;
  touch-action: pan-x;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const MobileProjectCard = styled.a`
  position: relative;
  flex: 0 0 min(82vw, 360px);
  min-height: 280px;
  scroll-snap-align: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px;
  color: var(--color-portfolio-silver-light);
  text-decoration: none;
  background: rgba(23, 17, 19, 0.95);
  border: 1px solid var(--color-portfolio-wine);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
  transform: translateZ(0);

  &:focus-visible {
    outline: 3px solid var(--color-portfolio-wine);
    outline-offset: 4px;
  }
`;

const MobileCardMeta = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: start;
  margin-bottom: 24px;
  font-family: var(--font-portfolio-mono);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-portfolio-wine-light);
`;

const MobileCardNumber = styled.span`
  color: var(--color-portfolio-silver);
`;

const MobileCardTitle = styled.h4`
  margin: 0 0 16px;
  font-family: var(--font-portfolio-display);
  font-size: clamp(2rem, 10vw, 3.1rem);
  line-height: 0.9;
  letter-spacing: 0;
  text-transform: uppercase;
`;

const MobileCardText = styled.p`
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.45;
  color: var(--color-portfolio-silver);
`;

const MobileTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 18px;
`;

const MobileTag = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 10px;
  border: 1px solid var(--color-portfolio-wine);
  font-family: var(--font-portfolio-mono);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-portfolio-silver-light);
  background: rgba(117, 22, 40, 0.2);
`;

const MobileCardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 26px;
  padding-top: 16px;
  border-top: 1px solid rgba(117, 22, 40, 0.3);
  font-family: var(--font-portfolio-mono);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-portfolio-wine-light);
`;

const MobileSwipeHint = styled.div`
  padding: 12px 20px 0;
  font-family: var(--font-portfolio-mono);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-portfolio-silver);
  pointer-events: none;
`;

const Projects = () => {
  const { containerRef: scrollContainerRef } = useContext(ScrollContext);
  const reduceMotion = useReducedMotion();
  const [gateStage, setGateStage] = useState(GATE_STAGE.CLOSED);
  const projectsRef = useRef(null);
  const enterButtonRef = useRef(null);
  const exitButtonRef = useRef(null);
  const hasEnteredRef = useRef(false);
  const focusFrameRef = useRef(null);
  const sceneActive = gateStage === GATE_STAGE.ACTIVE;
  const gateOpaque = gateStage === GATE_STAGE.CLOSED
    || gateStage === GATE_STAGE.FOCUSING
    || gateStage === GATE_STAGE.CLOSING;
  const gateInteractive = gateStage === GATE_STAGE.CLOSED;
  const scrollLocked = gateStage !== GATE_STAGE.CLOSED;

  const openGate = useCallback(() => {
    if (gateStage !== GATE_STAGE.CLOSED) return;

    const scroller = scrollContainerRef?.current;
    const section = projectsRef.current;

    if (scroller && section) {
      const scrollerRect = scroller.getBoundingClientRect();
      const sectionRect = section.getBoundingClientRect();
      const desiredScrollTop = scroller.scrollTop + sectionRect.top - scrollerRect.top;
      const maxScrollTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
      scroller.scrollTop = Math.min(Math.max(0, desiredScrollTop), maxScrollTop);
    }

    hasEnteredRef.current = true;

    if (reduceMotion) {
      setGateStage(GATE_STAGE.ACTIVE);
      return;
    }

    setGateStage(GATE_STAGE.FOCUSING);
    focusFrameRef.current = window.requestAnimationFrame(() => {
      focusFrameRef.current = null;
      setGateStage(GATE_STAGE.OPENING);
    });
  }, [gateStage, reduceMotion, scrollContainerRef]);

  const closeGate = useCallback(() => {
    if (gateStage !== GATE_STAGE.ACTIVE) return;
    setGateStage(reduceMotion ? GATE_STAGE.CLOSED : GATE_STAGE.CLOSING);
  }, [gateStage, reduceMotion]);

  useEffect(() => {
    return () => {
      if (focusFrameRef.current !== null) {
        window.cancelAnimationFrame(focusFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!scrollLocked) return undefined;

    const scroller = scrollContainerRef?.current;
    if (!scroller) return undefined;

    const lockedScrollTop = scroller.scrollTop;
    const previousStyles = {
      overflowY: scroller.style.overflowY,
      overscrollBehavior: scroller.style.overscrollBehavior,
      touchAction: scroller.style.touchAction,
    };

    scroller.style.overflowY = 'hidden';
    scroller.style.overscrollBehavior = 'none';
    scroller.style.touchAction = 'none';

    const holdScrollPosition = () => {
      if (scroller.scrollTop !== lockedScrollTop) {
        scroller.scrollTop = lockedScrollTop;
      }
    };

    scroller.addEventListener('scroll', holdScrollPosition, { passive: true });

    return () => {
      scroller.removeEventListener('scroll', holdScrollPosition);
      scroller.style.overflowY = previousStyles.overflowY;
      scroller.style.overscrollBehavior = previousStyles.overscrollBehavior;
      scroller.style.touchAction = previousStyles.touchAction;
      scroller.scrollTop = lockedScrollTop;
    };
  }, [scrollContainerRef, scrollLocked]);

  useEffect(() => {
    if (!sceneActive) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeGate();
    };

    window.addEventListener('keydown', handleKeyDown);
    window.requestAnimationFrame(() => exitButtonRef.current?.focus());

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeGate, sceneActive]);

  useEffect(() => {
    if (gateStage !== GATE_STAGE.CLOSED || !hasEnteredRef.current) return;
    window.requestAnimationFrame(() => enterButtonRef.current?.focus({ preventScroll: true }));
  }, [gateStage]);

  const finishGateTransition = () => {
    if (gateStage === GATE_STAGE.OPENING) {
      setGateStage(GATE_STAGE.ACTIVE);
    } else if (gateStage === GATE_STAGE.CLOSING) {
      setGateStage(GATE_STAGE.CLOSED);
    }
  };

  return (
    <ProjectsContainer ref={projectsRef} id="projects" aria-labelledby="projects-title">
      <ProjectsTitle id="projects-title">Selected Work</ProjectsTitle>
      
      <SectionBadge aria-hidden={!sceneActive} style={{ opacity: sceneActive ? 1 : 0 }}>
        <BadgeTag>05 // Portfolio 3D</BadgeTag>
        <BadgeTitle>Selected <span>Work</span></BadgeTitle>
      </SectionBadge>

      <SceneLayer $active={sceneActive} aria-hidden={!sceneActive}>
        <Projects3D isActive={sceneActive} />
      </SceneLayer>

      <GateLayer
        initial={false}
        animate={{ opacity: gateOpaque ? 1 : 0 }}
        transition={{
          duration: reduceMotion ? 0 : GATE_TIMING.transition,
          ease: gateOpaque ? GATE_EASE : [0.4, 0, 1, 1],
        }}
        $interactive={gateInteractive}
        aria-hidden={!gateInteractive}
        onAnimationComplete={finishGateTransition}
      >
        <GateAtmosphere />
        <GateFocusMist aria-hidden="true" />
        <GateImage
          src="/assets/entery gate projects.png"
          width="1672"
          height="941"
          alt=""
          draggable="false"
          initial={false}
          animate={{
            x: '-50%',
            y: gateStage === GATE_STAGE.OPENING ? '5%' : '0%',
            scale: gateStage === GATE_STAGE.OPENING ? 1.2 : 1,
          }}
          transition={reduceMotion ? { duration: 0 } : GATE_SPRING}
        />
        <GateKicker
          initial={false}
          animate={{ opacity: gateStage === GATE_STAGE.OPENING ? 0 : 1 }}
          transition={{ duration: reduceMotion ? 0 : GATE_TIMING.copy }}
        >
          05 // The Ancient Arch
        </GateKicker>
        <GateContent
          initial={false}
          animate={{
            opacity: gateStage === GATE_STAGE.OPENING ? 0 : 1,
            y: gateStage === GATE_STAGE.OPENING ? -14 : 0,
            scale: gateStage === GATE_STAGE.OPENING ? 0.97 : 1,
          }}
          transition={{
            duration: reduceMotion ? 0 : GATE_TIMING.copy,
            ease: GATE_EASE,
          }}
        >
          <GateNumber aria-hidden="true">05</GateNumber>
          <GateEyebrow>Enter the realm of</GateEyebrow>
          <GateTitle>Selected Work</GateTitle>
          <GateCopy>
            Where ideas take form. Step through to explore the work in 3D.
          </GateCopy>
          <EnterButton
            ref={enterButtonRef}
            type="button"
            onClick={openGate}
            disabled={!gateInteractive}
            whileHover={reduceMotion ? undefined : { scale: 1.035 }}
            whileTap={reduceMotion ? undefined : { scale: 0.97 }}
            transition={{ duration: GATE_TIMING.button }}
          >
            Enter
          </EnterButton>
        </GateContent>
      </GateLayer>

      {sceneActive && (
        <ExitButton
          ref={exitButtonRef}
          type="button"
          onClick={closeGate}
          aria-label="Exit project exploration and restore page scrolling"
          initial={reduceMotion ? false : { opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={reduceMotion ? { duration: 0 } : GATE_SPRING}
        >
          Exit projects
        </ExitButton>
      )}
      
      <MobileProjectsLayer aria-label="Projects">
        <MobileHeader>
          <MobileEyebrow>05 // Portfolio 3D</MobileEyebrow>
          <MobileTitle>Selected Work</MobileTitle>
        </MobileHeader>
        <MobileScroller>
          {PROJECTS.map((project, index) => (
            <MobileProjectCard
              key={`${project.title}-${index}`}
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div>
                <MobileCardMeta>
                  <span>{project.category || 'Featured Project'} - {project.date}</span>
                  <MobileCardNumber>{String(index + 1).padStart(2, '0')}</MobileCardNumber>
                </MobileCardMeta>
                <MobileCardTitle>{project.title}</MobileCardTitle>
                <MobileCardText>{project.subtext}</MobileCardText>
                <MobileTags>
                  {(project.stack || []).slice(0, 3).map((tag) => (
                    <MobileTag key={tag}>{tag}</MobileTag>
                  ))}
                </MobileTags>
              </div>
              <MobileCardFooter>
                <span>View project</span>
                <span aria-hidden="true">-&gt;</span>
              </MobileCardFooter>
            </MobileProjectCard>
          ))}
        </MobileScroller>
        <MobileSwipeHint aria-hidden="true">Swipe sideways</MobileSwipeHint>
      </MobileProjectsLayer>
      {sceneActive && (
        <PanHint aria-hidden="true">DRAG / PAN TO EXPLORE 3D SPACE</PanHint>
      )}
    </ProjectsContainer>
  );
};

export default Projects;
